/**
 * ErrorBoundary — захищає від «білої сторінки» при runtime-помилці у дереві.
 *
 * React за замовчуванням розмонтовує весь компонент при unhandled-throw.
 * Цей boundary показує fallback-UI + кнопку «Перезавантажити».
 *
 * Використання:
 *   <ErrorBoundary>
 *     <YourPage />
 *   </ErrorBoundary>
 */
import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <div
          style={{
            padding: '2rem',
            maxWidth: 600,
            margin: '2rem auto',
            background: 'var(--bg-panel, #fff)',
            border: '1px solid var(--danger, #dc2626)',
            borderRadius: 'var(--radius, 8px)',
          }}
        >
          <h2 style={{ marginTop: 0, color: 'var(--danger, #dc2626)' }}>
            ⚠ Помилка
          </h2>
          <p>На сторінці сталась непередбачена помилка.</p>
          <details style={{ marginTop: '1rem' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--text-secondary, #475569)' }}>
              Технічна інформація
            </summary>
            <pre
              style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                background: 'var(--bg-soft, #f1f5f9)',
                borderRadius: 'var(--radius-sm, 6px)',
                fontSize: '0.8rem',
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              {this.state.error.name}: {this.state.error.message}
              {this.state.error.stack && `\n\n${this.state.error.stack}`}
            </pre>
          </details>
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={this.reset}>↻ Спробувати ще</button>
            <button onClick={() => window.location.reload()} className="primary">
              🔄 Перезавантажити сторінку
            </button>
            <button onClick={() => window.history.back()}>← Назад</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
